import React from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { ActiveOrgProvider, useActiveOrg } from './contexts/ActiveOrgContext';
import Landing from './pages/Landing';
import Login from './Login';
import MagicLinkVerify from './pages/MagicLinkVerify';
import DashboardLayout from './layouts/DashboardLayout';
import Dashboard from './pages/Dashboard';
import Robots from './pages/Robots';
import Jobs from './pages/Jobs';
import Settings from './pages/Settings';
import Organizations from './pages/Organizations';
import Vault from './pages/Vault';
import Reports from './pages/Reports';
import Customers from './pages/Customers';

// Basit Auth Guard
const ProtectedRoute = ({ children }) => {
  const token = localStorage.getItem('token');
  if (!token) {
    return <Navigate to="/login" replace />;
  }
  return children;
};

// Akıllı Kök Rota Guard (Giriş yaptıysa dashboard, yapmadıysa landing)
const RootRoute = () => {
  const token = localStorage.getItem('token');
  if (token) {
    return <Navigate to="/dashboard" replace />;
  }
  return <Landing />;
};

// Organizasyon seçimi gerektiren sayfalar için guard
const OrgGuard = ({ children }) => {
  const { needsOrgSelection, loading } = useActiveOrg();
  const location = useLocation();

  // Organizations sayfasına her zaman izin ver
  if (location.pathname === '/organizations') {
    return children;
  }

  // Yükleniyorsa bekle
  if (loading) {
    return (
      <div className="min-h-screen bg-slate-950 flex flex-col items-center justify-center p-8">
        <div className="flex flex-col items-center justify-center animate-pulse">
          <img src="/logo.png" alt="Robopam Logo" className="w-24 h-24 object-contain drop-shadow-[0_0_25px_rgba(6,182,212,0.6)] mb-4" />
          <span className="text-2xl font-black tracking-widest bg-gradient-to-r from-cyan-400 via-indigo-300 to-indigo-400 bg-clip-text text-transparent mb-6">
            ROBOPAM
          </span>
          <div className="flex items-center gap-3 text-cyan-400 font-mono text-sm border border-slate-800 bg-slate-900/80 px-6 py-3 rounded-full shadow-lg">
            <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-cyan-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            Sistem Yükleniyor...
          </div>
        </div>
      </div>
    );
  }

  // Org seçimi gerekiyorsa Organizations sayfasına yönlendir
  if (needsOrgSelection) {
    return <Navigate to="/organizations" replace />;
  }

  return children;
};

function AppRoutes() {
  return (
    <Routes>
      <Route path="/" element={<RootRoute />} />
      <Route path="/login" element={<Login />} />
      <Route path="/auth/magic-link/:token" element={<MagicLinkVerify />} />

      <Route element={
        <ProtectedRoute>
          <DashboardLayout />
        </ProtectedRoute>
      }>
        <Route path="/dashboard" element={<OrgGuard><Dashboard /></OrgGuard>} />
        <Route path="/robots" element={<OrgGuard><Robots /></OrgGuard>} />
        <Route path="/jobs" element={<OrgGuard><Jobs /></OrgGuard>} />
        <Route path="/organizations" element={<Organizations />} />
        <Route path="/customers" element={<OrgGuard><Customers /></OrgGuard>} />
        <Route path="/reports" element={<OrgGuard><Reports /></OrgGuard>} />
        <Route path="/vault" element={<OrgGuard><Vault /></OrgGuard>} />
        <Route path="/settings" element={<OrgGuard><Settings /></OrgGuard>} />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <ActiveOrgProvider>
        <AppRoutes />
      </ActiveOrgProvider>
    </BrowserRouter>
  );
}

export default App;
